import { type ActivityCampaign, ActivityStatus } from "@/types/activity";

/**
 * 根据时间和数据库状态判断活动的实际状态
 */
export function getActualActivityStatus(
	campaign: ActivityCampaign,
): ActivityStatus {
	const now = new Date();
	const startTime = new Date(campaign.startTime);
	const endTime = new Date(campaign.endTime);

	// 如果手动设置为已取消或已结束，直接返回
	if (campaign.status === ActivityStatus.CANCELLED) {
		return ActivityStatus.CANCELLED;
	}

	if (campaign.status === ActivityStatus.ENDED) {
		return ActivityStatus.ENDED;
	}

	// 根据时间判断状态
	if (now < startTime) {
		// 还未到开始时间
		return campaign.status === ActivityStatus.DRAFT
			? ActivityStatus.DRAFT
			: ActivityStatus.PENDING;
	} else if (now >= startTime && now <= endTime) {
		// 在活动时间范围内
		return campaign.status === ActivityStatus.DRAFT
			? ActivityStatus.DRAFT
			: ActivityStatus.ACTIVE;
	} else {
		// 超过结束时间，自动视为已结束
		return ActivityStatus.ENDED;
	}
}

/**
 * 根据实际状态获取状态文本
 */
export function getStatusText(
	status: ActivityStatus,
	language: "en" | "zh",
): string {
	const texts = {
		[ActivityStatus.DRAFT]: language === "zh" ? "草稿" : "Draft",
		[ActivityStatus.PENDING]: language === "zh" ? "未开始" : "Pending",
		[ActivityStatus.ACTIVE]: language === "zh" ? "进行中" : "Active",
		[ActivityStatus.ENDED]: language === "zh" ? "已结束" : "Ended",
		[ActivityStatus.CANCELLED]: language === "zh" ? "已取消" : "Cancelled",
	};
	return texts[status] || status;
}

/**
 * 根据实际状态获取状态颜色
 */
export function getStatusColor(status: ActivityStatus): string {
	switch (status) {
		case ActivityStatus.DRAFT:
			return "text-gray-500";
		case ActivityStatus.PENDING:
			return "text-yellow-500";
		case ActivityStatus.ACTIVE:
			return "text-green-500";
		case ActivityStatus.ENDED:
			return "text-blue-500";
		case ActivityStatus.CANCELLED:
			return "text-red-500";
		default:
			return "text-gray-500";
	}
}

/**
 * 判断活动是否可以参与
 */
export function canJoinActivity(
	campaign: ActivityCampaign,
	isAuthenticated: boolean,
): boolean {
	if (!isAuthenticated) return false;
	const actualStatus = getActualActivityStatus(campaign);
	if (actualStatus !== ActivityStatus.ACTIVE) return false;
	if (campaign.isParticipant) return false;
	if (
		campaign.maxParticipants &&
		campaign.currentParticipants >= campaign.maxParticipants
	)
		return false;
	return true;
}

/**
 * 扩展活动对象，添加实际状态信息
 */
export function enrichCampaignWithActualStatus(
	campaign: ActivityCampaign,
): ActivityCampaign & { actualStatus: ActivityStatus } {
	return {
		...campaign,
		actualStatus: getActualActivityStatus(campaign),
	};
}

/**
 * 过滤活动列表，根据实际状态分类
 */
export function filterCampaignsByActualStatus(
	campaigns: ActivityCampaign[],
	status: ActivityStatus,
): ActivityCampaign[] {
	return campaigns.filter(
		(campaign) => getActualActivityStatus(campaign) === status,
	);
}
